import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FloppyDisk, Plus, Trash, Pencil, X, Robot, Chats } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";

/* EstimaBot Control Room panel.

   Three sub-tabs:
     • Settings  — enable, name, welcome message, voice, avatar URL, body image
     • KB        — list / add / edit / delete Q&A pairs
     • Feature info — list / upsert per-feature info cards
     • Feedback  — read-only list of exit-intent submissions

   Lives under /app/admin → Control room → Estima bot. */
export default function ChatbotControlPanel() {
  const [tab, setTab] = useState("settings");
  const [coverage, setCoverage] = useState(null);

  const loadCoverage = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/chatbot/coverage");
      setCoverage(data || {});
    } catch { /* silent — pill just stays hidden */ }
  }, []);

  useEffect(() => { loadCoverage(); }, [loadCoverage]);

  return (
    <div className="space-y-4" data-testid="chatbot-control-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow inline-flex items-center gap-2">
            <Robot size={12} weight="duotone" /> Estima onboarding bot
          </div>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            The bot stays hidden from the public until you flip <b>Enable</b> below.
            KB + Feature info are admin-editable and auto-refresh the bot's answers.
          </p>
        </div>
        <CoveragePill coverage={coverage} onRefresh={loadCoverage} />
      </div>

      <div className="inline-flex border hairline rounded overflow-hidden text-xs"
        data-testid="chatbot-control-tabs">
        {[
          ["settings", "Settings"],
          ["kb", "Knowledge base"],
          ["features", "Feature info"],
          ["feedback", "Exit feedback"],
        ].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            data-testid={`chatbot-control-tab-${id}`}
            className={`px-3 py-1.5 ${tab === id ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-muted/40"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "settings" && <SettingsTab />}
      {tab === "kb"       && <KbTab />}
      {tab === "features" && <FeatureInfoTab />}
      {tab === "feedback" && <FeedbackTab />}
    </div>
  );
}

/* ------------------------------ Coverage pill ----------------------------- */
function CoveragePill({ coverage, onRefresh }) {
  if (!coverage) return null;
  const pct = coverage.coverage_pct;
  const total = coverage.total ?? 0;
  // Color: green >=80, amber 50-80, red <50, gray when no data yet
  let tone = "border hairline text-muted-foreground bg-muted/30";
  let dot = "bg-muted-foreground";
  if (pct != null) {
    if (pct >= 80)      { tone = "border-green-500/40 text-green-600 bg-green-500/10";  dot = "bg-green-500"; }
    else if (pct >= 50) { tone = "border-amber-500/40 text-amber-600 bg-amber-500/10"; dot = "bg-amber-500"; }
    else                 { tone = "border-red-500/40 text-red-600 bg-red-500/10";       dot = "bg-red-500"; }
  }
  return (
    <button
      onClick={onRefresh}
      title={pct == null
        ? "No questions asked yet — coverage will appear after first user query."
        : `${coverage.answered}/${total} questions answered from KB. Click to refresh.`}
      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] border ${tone} hover:opacity-90 transition-opacity`}
      data-testid="chatbot-coverage-pill"
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      <span>
        KB coverage:{" "}
        <b data-testid="chatbot-coverage-pct">
          {pct == null ? "—" : `${pct}%`}
        </b>
        {total > 0 && <span className="opacity-70"> · {coverage.answered}/{total}</span>}
      </span>
    </button>
  );
}

/* -------------------------------- Settings ------------------------------ */
function SettingsTab() {
  const [cfg, setCfg] = useState(null);
  const [voices, setVoices] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/chatbot/config").then((r) => setCfg(r.data || {}));
    // Web Speech voices are populated async; subscribe.
    const load = () => setVoices(window.speechSynthesis?.getVoices?.() || []);
    load();
    window.speechSynthesis?.addEventListener?.("voiceschanged", load);
    return () => window.speechSynthesis?.removeEventListener?.("voiceschanged", load);
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.post("/admin/chatbot/config", patch);
      setCfg((c) => ({ ...c, ...patch }));
      toast.success("Saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!cfg) return <div className="text-xs text-muted-foreground">Loading…</div>;

  return (
    <div className="card-flat p-4 space-y-4" data-testid="chatbot-settings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-medium">Bot status</div>
          <p className="text-[11px] text-muted-foreground mt-1">
            When enabled, the bot appears on the Landing + Pricing pages.
          </p>
        </div>
        <button
          onClick={() => save({ enabled: !cfg.enabled })}
          disabled={saving}
          className={`px-4 py-1.5 text-xs rounded ${cfg.enabled ? "bg-[hsl(var(--primary))] text-white" : "border hairline"}`}
          data-testid="chatbot-enabled-toggle">
          {cfg.enabled ? "Enabled · click to disable" : "Disabled · click to enable"}
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Bot display name" value={cfg.name || ""}
          onSave={(v) => save({ name: v })}
          testid="chatbot-name" />
        <Field label="Avatar URL (small square icon)" value={cfg.avatar_url || ""}
          onSave={(v) => save({ avatar_url: v })}
          testid="chatbot-avatar" />
        <Field label="Body image URL (full bot illustration)" value={cfg.body_image_url || ""}
          onSave={(v) => save({ body_image_url: v })}
          testid="chatbot-body-image" />
      </div>

      <div>
        <div className="text-xs font-medium mb-1">Welcome message</div>
        <textarea defaultValue={cfg.welcome_message} rows={2}
          onBlur={(e) => e.target.value !== cfg.welcome_message && save({ welcome_message: e.target.value })}
          className="w-full bg-background border hairline px-2 py-1.5 text-xs"
          data-testid="chatbot-welcome" />
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <div className="text-xs font-medium mb-1">Voice (Web Speech API)</div>
          <div className="inline-flex items-center gap-2 mb-1">
            <input type="checkbox" checked={!!cfg.voice_enabled}
              onChange={(e) => save({ voice_enabled: e.target.checked })}
              data-testid="chatbot-voice-enabled" />
            <span className="text-[11px]">Enable voice replies (browser TTS — free)</span>
          </div>
          <select value={cfg.voice_name || ""}
            onChange={(e) => save({ voice_name: e.target.value })}
            disabled={!cfg.voice_enabled}
            className="w-full bg-background border hairline px-2 py-1.5 text-xs"
            data-testid="chatbot-voice-name">
            <option value="">Browser default</option>
            {voices.map((v) => <option key={v.name} value={v.name}>{v.name} ({v.lang})</option>)}
          </select>
        </div>
        <div>
          <div className="text-xs font-medium mb-1">Behaviour</div>
          <Toggle label="Follow cursor on exit-intent"
            checked={!!cfg.follow_cursor_exit}
            onChange={(v) => save({ follow_cursor_exit: v })}
            testid="chatbot-follow-cursor" />
          <Toggle label="Show 'Create free account' CTA"
            checked={!!cfg.show_free_account_cta}
            onChange={(v) => save({ show_free_account_cta: v })}
            testid="chatbot-cta" />
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onSave, testid }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <label className="text-[11px]">
      <span className="text-muted-foreground">{label}</span>
      <input value={v} onChange={(e) => setV(e.target.value)}
        onBlur={() => v !== value && onSave(v)}
        className="w-full bg-background border hairline px-2 py-1.5 text-xs mt-0.5"
        data-testid={testid} />
    </label>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 text-[11px] mb-1">
      <input type="checkbox" checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid} />
      <span>{label}</span>
    </label>
  );
}

/* ----------------------------- Knowledge base --------------------------- */
function KbTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/chatbot/kb");
    setItems(data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this KB entry?")) return;
    await api.delete(`/admin/chatbot/kb/${id}`);
    await load();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-sm font-medium">Knowledge base ({items.length})</div>
          <p className="text-[11px] text-muted-foreground">
            Hand-curated Q&A pairs. The bot retrieves these by keyword overlap.
          </p>
        </div>
        <button onClick={() => setEditing({ q: "", a: "", tags: [] })}
          className="btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
          data-testid="chatbot-kb-add">
          <Plus size={12} weight="bold" /> Add entry
        </button>
      </div>
      <div className="space-y-2">
        {items.map((e) => (
          <div key={e.id} className="card-flat p-3 flex items-start justify-between gap-2"
            data-testid={`chatbot-kb-row-${e.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{e.q}</div>
              <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{e.a}</div>
              {e.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {e.tags.map((t) => (
                    <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full border hairline">{t}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="inline-flex items-center gap-1">
              <button onClick={() => setEditing(e)} className="btn-ghost px-2 py-1 text-[10px]"
                data-testid={`chatbot-kb-edit-${e.id}`}>
                <Pencil size={10} weight="duotone" />
              </button>
              <button onClick={() => del(e.id)} className="btn-ghost px-2 py-1 text-[10px] text-destructive"
                data-testid={`chatbot-kb-delete-${e.id}`}>
                <Trash size={10} weight="bold" />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-xs text-muted-foreground italic py-4 text-center">
            No KB entries yet. Click "Add entry" to start.
          </div>
        )}
      </div>
      {editing && <KbEditor entry={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function KbEditor({ entry, onClose, onSaved }) {
  const [q, setQ] = useState(entry.q || "");
  const [a, setA] = useState(entry.a || "");
  const [tags, setTags] = useState((entry.tags || []).join(", "));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!q.trim() || !a.trim()) { toast.error("Both question and answer are required."); return; }
    setBusy(true);
    try {
      await api.post("/admin/chatbot/kb", {
        id: entry.id, q, a,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Saved.");
      onSaved(); onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-background border hairline max-w-2xl w-full p-5 space-y-3"
        data-testid="chatbot-kb-editor">
        <div className="flex items-baseline justify-between">
          <h3 className="font-serif text-xl">{entry.id ? "Edit" : "New"} KB entry</h3>
          <button onClick={onClose} className="btn-ghost px-2 py-1.5 text-xs"
            data-testid="chatbot-kb-editor-close"><X size={14} weight="bold" /></button>
        </div>
        <label className="text-[11px] block">
          <span className="text-muted-foreground">Question</span>
          <input value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full bg-background border hairline px-2 py-1.5 text-xs mt-0.5"
            data-testid="chatbot-kb-q" />
        </label>
        <label className="text-[11px] block">
          <span className="text-muted-foreground">Answer</span>
          <textarea value={a} onChange={(e) => setA(e.target.value)} rows={5}
            className="w-full bg-background border hairline px-2 py-1.5 text-xs mt-0.5"
            data-testid="chatbot-kb-a" />
        </label>
        <label className="text-[11px] block">
          <span className="text-muted-foreground">Tags (comma-separated)</span>
          <input value={tags} onChange={(e) => setTags(e.target.value)}
            className="w-full bg-background border hairline px-2 py-1.5 text-xs mt-0.5"
            data-testid="chatbot-kb-tags" placeholder="e.g. emi, calculators" />
        </label>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button onClick={onClose} className="btn-ghost px-3 py-1.5 text-xs"
            data-testid="chatbot-kb-cancel">Cancel</button>
          <button onClick={save} disabled={busy}
            className="btn-primary px-4 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="chatbot-kb-save">
            <FloppyDisk size={12} weight="duotone" />
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Feature info ----------------------------- */
function FeatureInfoTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/chatbot/feature-info");
    setItems(data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-sm font-medium">Feature info cards ({items.length})</div>
          <p className="text-[11px] text-muted-foreground">
            Each card backs the "i" icon on the feature page + improves bot answers.
          </p>
        </div>
        <button onClick={() => setEditing({ feature_slug: "", title: "" })}
          className="btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
          data-testid="chatbot-feature-add">
          <Plus size={12} weight="bold" /> Add card
        </button>
      </div>
      <div className="space-y-2">
        {items.map((f) => (
          <div key={f.id} className="card-flat p-3 flex items-start justify-between gap-2"
            data-testid={`chatbot-feature-row-${f.feature_slug}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{f.title} <span className="text-[10px] text-muted-foreground font-mono">/{f.feature_slug}</span></div>
              <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{f.purpose}</div>
              <div className="flex flex-wrap gap-1 mt-1 text-[10px] text-muted-foreground">
                {f.downloadable && <span>📥 Download</span>}
                {f.shareable && <span>🔗 Share</span>}
                {f.needs_login && <span>🔐 Login required</span>}
              </div>
            </div>
            <button onClick={() => setEditing(f)} className="btn-ghost px-2 py-1 text-[10px]"
              data-testid={`chatbot-feature-edit-${f.feature_slug}`}>
              <Pencil size={10} weight="duotone" />
            </button>
          </div>
        ))}
      </div>
      {editing && <FeatureEditor entry={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function FeatureEditor({ entry, onClose, onSaved }) {
  const [v, setV] = useState({
    feature_slug: entry.feature_slug || "",
    title: entry.title || "",
    purpose: entry.purpose || "",
    inputs: entry.inputs || "",
    outputs: entry.outputs || "",
    downloadable: !!entry.downloadable,
    shareable: !!entry.shareable,
    needs_login: !!entry.needs_login,
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!v.feature_slug.trim() || !v.title.trim()) { toast.error("Slug and title required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/chatbot/feature-info", v);
      toast.success("Saved.");
      onSaved(); onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const set = (k, val) => setV((prev) => ({ ...prev, [k]: val }));
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-background border hairline max-w-2xl w-full p-5 space-y-3 max-h-[92vh] overflow-y-auto"
        data-testid="chatbot-feature-editor">
        <div className="flex items-baseline justify-between">
          <h3 className="font-serif text-xl">{entry.id ? "Edit" : "New"} feature card</h3>
          <button onClick={onClose} className="btn-ghost px-2 py-1.5 text-xs"
            data-testid="chatbot-feature-editor-close"><X size={14} weight="bold" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-[11px]">
            <span className="text-muted-foreground">Feature slug (URL-safe, used as id)</span>
            <input value={v.feature_slug} onChange={(e) => set("feature_slug", e.target.value)}
              disabled={!!entry.id}
              placeholder="e.g. rent-vs-buy"
              className="w-full bg-background border hairline px-2 py-1.5 text-xs font-mono mt-0.5"
              data-testid="chatbot-feature-slug" />
          </label>
          <label className="text-[11px]">
            <span className="text-muted-foreground">Title</span>
            <input value={v.title} onChange={(e) => set("title", e.target.value)}
              className="w-full bg-background border hairline px-2 py-1.5 text-xs mt-0.5"
              data-testid="chatbot-feature-title" />
          </label>
        </div>
        <label className="text-[11px] block">
          <span className="text-muted-foreground">Purpose</span>
          <textarea value={v.purpose} onChange={(e) => set("purpose", e.target.value)} rows={2}
            className="w-full bg-background border hairline px-2 py-1.5 text-xs mt-0.5"
            data-testid="chatbot-feature-purpose" />
        </label>
        <label className="text-[11px] block">
          <span className="text-muted-foreground">Input fields the user provides</span>
          <textarea value={v.inputs} onChange={(e) => set("inputs", e.target.value)} rows={2}
            className="w-full bg-background border hairline px-2 py-1.5 text-xs mt-0.5"
            data-testid="chatbot-feature-inputs" />
        </label>
        <label className="text-[11px] block">
          <span className="text-muted-foreground">Expected outputs</span>
          <textarea value={v.outputs} onChange={(e) => set("outputs", e.target.value)} rows={2}
            className="w-full bg-background border hairline px-2 py-1.5 text-xs mt-0.5"
            data-testid="chatbot-feature-outputs" />
        </label>
        <div className="flex flex-wrap items-center gap-3 text-[11px]">
          <Toggle label="Downloadable report"  checked={v.downloadable} onChange={(x) => set("downloadable", x)}
            testid="chatbot-feature-downloadable" />
          <Toggle label="Shareable link (login)" checked={v.shareable} onChange={(x) => set("shareable", x)}
            testid="chatbot-feature-shareable" />
          <Toggle label="Needs login"          checked={v.needs_login} onChange={(x) => set("needs_login", x)}
            testid="chatbot-feature-needs-login" />
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button onClick={onClose} className="btn-ghost px-3 py-1.5 text-xs"
            data-testid="chatbot-feature-cancel">Cancel</button>
          <button onClick={save} disabled={busy}
            className="btn-primary px-4 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="chatbot-feature-save">
            <FloppyDisk size={12} weight="duotone" />
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Feedback log ----------------------------- */
function FeedbackTab() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/chatbot/feedback").then((r) => setItems(r.data.items || []));
  }, []);
  return (
    <div className="space-y-2">
      <div className="text-sm font-medium inline-flex items-center gap-1.5">
        <Chats size={12} weight="duotone" /> Exit-intent feedback ({items.length})
      </div>
      <p className="text-[11px] text-muted-foreground">
        What visitors said when they were about to leave the site. Improve KB or
        ship new features based on patterns.
      </p>
      {items.length === 0 ? (
        <div className="text-xs text-muted-foreground italic py-4 text-center"
          data-testid="chatbot-feedback-empty">
          No feedback yet — once visitors interact with the exit modal, you'll see it here.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((e) => (
            <div key={e.id} className="card-flat p-3 text-xs"
              data-testid={`chatbot-feedback-row-${e.id}`}>
              <div>{e.text}</div>
              <div className="text-[10px] text-muted-foreground mt-1">
                {e.page || "?"} · {e.email || "no email"} · {e.at}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
